import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { Link } from 'react-router-dom';
import { CheckSquare, StickyNote, Paperclip, Download } from 'lucide-react';
import { apiDownloadUrl } from '../../lib/api';
import type { JSONContent } from '@tiptap/react';
import { GridCard } from './GridCard';
import { MeetingGroupCard, type MeetingGroup } from './MeetingGroupCard';
import { AnnotationCard } from './AnnotationCard';
import type { GridCardConfig } from './layoutUtils';

interface ViewContentItem {
  source_type: 'meeting_instance' | 'task' | 'note' | 'attachment';
  source_id: string;
  title: string;
  description?: string | null;
  tag_name: string;
  tag_id: string;
  tag_color: string;
  meeting_date?: string;
  series_id?: string;
  series_name?: string;
  tagged_text?: string;
  block_id?: string | null;
  note_tag_id?: string;
  status?: string;
  priority?: string;
  content?: string | null;
  created_at?: string;
  updated_at?: string;
  filename?: string;
  mime_type?: string;
  size_bytes?: number;
}

interface Annotation {
  id: string;
  after_item_id: string | null;
  content: JSONContent;
}

interface ViewGridLayoutProps {
  cards: GridCardConfig[];
  meetingGroups: MeetingGroup[];
  taskItems: ViewContentItem[];
  noteItems: ViewContentItem[];
  fileItems: ViewContentItem[];
  annotations: Annotation[];
  onReorder: (cards: GridCardConfig[]) => void;
  onToggleSpan: (cardId: string) => void;
  onUpdateAnnotation: (id: string, content: JSONContent) => void;
  onDeleteAnnotation: (id: string) => void;
  onFilePreview: (att: { id: string; filename: string; mime_type: string }) => void;
}

const statusColors: Record<string, string> = {
  todo: '#64748b',
  in_progress: '#f59e0b',
  done: '#22c55e',
  blocked: '#ef4444',
};

export function ViewGridLayout({
  cards,
  meetingGroups,
  taskItems,
  noteItems,
  fileItems,
  annotations,
  onReorder,
  onToggleSpan,
  onUpdateAnnotation,
  onDeleteAnnotation,
  onFilePreview,
}: ViewGridLayoutProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Build lookup maps
  const meetingGroupMap = new Map(meetingGroups.map(g => [g.meeting_instance_id, g]));
  const annotationMap = new Map(annotations.map(a => [a.id, a]));

  // Deduplicate tasks/notes by source_id
  const taskMap = new Map<string, ViewContentItem>();
  for (const t of taskItems) {
    if (!taskMap.has(t.source_id)) taskMap.set(t.source_id, t);
  }
  const noteMap = new Map<string, ViewContentItem>();
  for (const n of noteItems) {
    if (!noteMap.has(n.source_id)) noteMap.set(n.source_id, n);
  }
  const fileMap = new Map<string, ViewContentItem>();
  for (const f of fileItems) {
    if (!fileMap.has(f.source_id)) fileMap.set(f.source_id, f);
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = cards.findIndex(c => c.id === active.id);
    const newIndex = cards.findIndex(c => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(cards, oldIndex, newIndex));
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  function renderCardContent(card: GridCardConfig) {
    switch (card.type) {
      case 'meeting_group': {
        const group = meetingGroupMap.get(card.ref_id);
        if (!group) return <div className="text-text-muted text-xs">Meeting not found</div>;
        return <MeetingGroupCard group={group} />;
      }
      case 'task': {
        const task = taskMap.get(card.ref_id);
        if (!task) return <div className="text-text-muted text-xs">Task not found</div>;
        return <TaskCardContent item={task} />;
      }
      case 'note': {
        const note = noteMap.get(card.ref_id);
        if (!note) return <div className="text-text-muted text-xs">Note not found</div>;
        return <NoteCardContent item={note} />;
      }
      case 'file': {
        const file = fileMap.get(card.ref_id);
        if (!file) return <div className="text-text-muted text-xs">File not found</div>;
        return <FileCardContent item={file} onPreview={onFilePreview} />;
      }
      case 'annotation': {
        const ann = annotationMap.get(card.ref_id);
        if (!ann) return <div className="text-text-muted text-xs">Annotation not found</div>;
        return (
          <AnnotationCard
            annotation={ann}
            onUpdate={onUpdateAnnotation}
            onDelete={onDeleteAnnotation}
          />
        );
      }
      default:
        return null;
    }
  }

  const activeCard = activeId ? cards.find(c => c.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={cards.map(c => c.id)} strategy={rectSortingStrategy}>
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-4 rounded-xl p-2 -m-2 transition-colors duration-200 ${
            activeId
              ? 'bg-[radial-gradient(circle,_var(--color-text-muted)_1px,_transparent_1px)] bg-[length:24px_24px] bg-bg-surface/50'
              : ''
          }`}
        >
          {cards.map(card => (
            <GridCard
              key={card.id}
              id={card.id}
              span={card.span}
              onToggleSpan={onToggleSpan}
            >
              {renderCardContent(card)}
            </GridCard>
          ))}
        </div>
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeCard ? (
          <div className={`${activeCard.span === 2 ? '' : ''} pointer-events-none`}>
            <div className="bg-bg-card border-2 border-accent rounded-lg p-4 shadow-lg shadow-accent/10 opacity-90">
              {renderCardContent(activeCard)}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function TaskCardContent({ item }: { item: ViewContentItem }) {
  const statusColor = statusColors[item.status ?? ''] ?? '#64748b';
  return (
    <Link
      to={`/tasks/${item.source_id}`}
      className="block hover:opacity-80 transition-opacity"
    >
      <div className="flex items-center gap-2 mb-1">
        <CheckSquare size={14} className="text-text-muted flex-shrink-0" />
        <span
          className="text-[10px] px-1.5 py-0.5 rounded uppercase font-medium"
          style={{ backgroundColor: statusColor + '20', color: statusColor }}
        >
          {(item.status ?? 'todo').replace('_', ' ')}
        </span>
      </div>
      <div className="flex items-start justify-between gap-2 mt-1">
        <span className="text-sm font-medium text-text-primary">{item.title}</span>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
        >
          {item.tag_name}
        </span>
      </div>
      {item.description && (
        <p className="text-xs text-text-muted line-clamp-2 mt-1">{extractPlainText(item.description)}</p>
      )}
    </Link>
  );
}

function NoteCardContent({ item }: { item: ViewContentItem }) {
  return (
    <Link
      to={`/notes/${item.source_id}`}
      className="block hover:opacity-80 transition-opacity"
    >
      <div className="flex items-center gap-2 mb-1">
        <StickyNote size={14} className="text-text-muted flex-shrink-0" />
        <span className="text-xs text-text-muted uppercase tracking-wider">Note</span>
      </div>
      <div className="flex items-start justify-between gap-2 mt-1">
        <span className="text-sm font-medium text-text-primary">{item.title}</span>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
        >
          {item.tag_name}
        </span>
      </div>
      {item.updated_at && (
        <p className="text-xs text-text-muted mt-1">
          Updated {new Date(item.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
      )}
    </Link>
  );
}

/** Extract plain text from a TipTap JSON string or return as-is if plain text */
function extractPlainText(content: string | null | undefined): string {
  if (!content) return '';
  if (!content.startsWith('{')) return content;
  try {
    const doc = JSON.parse(content);
    return extractTextFromNode(doc).trim();
  } catch {
    return content;
  }
}
function extractTextFromNode(node: any): string {
  if (!node) return '';
  let text = '';
  if (node.text) text += node.text;
  if (node.content && Array.isArray(node.content)) {
    for (const child of node.content) {
      text += extractTextFromNode(child) + ' ';
    }
  }
  return text;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const GRID_DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function isGridPreviewable(mimeType: string): boolean {
  return mimeType.startsWith('image/') || mimeType === 'application/pdf' || mimeType === 'message/rfc822' || mimeType === GRID_DOCX_MIME;
}

function FileCardContent({ item, onPreview }: { item: ViewContentItem; onPreview: (att: { id: string; filename: string; mime_type: string }) => void }) {
  const mime = item.mime_type ?? '';
  const previewable = isGridPreviewable(mime);

  async function handleClick() {
    if (previewable) {
      onPreview({ id: item.source_id, filename: item.filename ?? item.title, mime_type: mime });
    } else {
      try {
        const blobUrl = await apiDownloadUrl(`/attachments/${item.source_id}/download`);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = item.filename ?? item.title;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      } catch (err) {
        console.error('Download failed:', err);
      }
    }
  }

  return (
    <button
      onClick={handleClick}
      className="block w-full text-left hover:opacity-80 transition-opacity cursor-pointer"
    >
      <div className="flex items-center gap-2 mb-1">
        <Paperclip size={14} className="text-text-muted flex-shrink-0" />
        <span className="text-xs text-text-muted uppercase tracking-wider">File</span>
        {item.size_bytes != null && (
          <span className="text-xs text-text-muted">{formatFileSize(item.size_bytes)}</span>
        )}
        <Download size={12} className="text-text-muted ml-auto" />
      </div>
      <div className="flex items-start justify-between gap-2 mt-1">
        <span className="text-sm font-medium text-text-primary">{item.filename ?? item.title}</span>
        {item.tag_name && (
          <span
            className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
          >
            {item.tag_name}
          </span>
        )}
      </div>
      {item.mime_type && (
        <p className="text-xs text-text-muted mt-1">{item.mime_type}</p>
      )}
    </button>
  );
}
